/**
 * AuthContext · état d'authentification + flow login + auto-refresh.
 *
 * Au boot de l'app, on tente un refresh silencieux (cookie httpOnly).
 * - Si succès : on est connecté, on récupère /me, on planifie le prochain refresh.
 * - Si 401 : on reste anonyme, l'utilisateur ira sur /login.
 *
 * L'access token reste en mémoire (pas localStorage / sessionStorage).
 * Le cookie refresh est httpOnly, géré côté API.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { LoginInput, LoginResponse, MeResponse } from "@victor/api-client";
import { isLoginMfaChallenge } from "@victor/types";
import {
  api,
  getAccessToken,
  onUnauthorized,
  setAccessToken,
} from "../lib/api";
import { clearOfflineDb } from "../lib/offline-db";

type User = MeResponse["user"];

type AuthStatus = "loading" | "anonymous" | "authenticated";

type AuthCtx = {
  status: AuthStatus;
  user: User | null;
  login: (input: LoginInput) => Promise<LoginResponse>;
  completeMfa: (mfaToken: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshMe: () => Promise<void>;
};

const AuthContext = createContext<AuthCtx | null>(null);

export function useAuth(): AuthCtx {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth doit être utilisé dans <AuthProvider>");
  return ctx;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [user, setUser] = useState<User | null>(null);
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleRefresh = useCallback((accessExpiresIn: number) => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    // Refresh 30s avant l'expiration de l'access token.
    const ms = Math.max(5_000, (accessExpiresIn - 30) * 1000);
    refreshTimer.current = setTimeout(() => {
      void doRefresh();
    }, ms);
  }, []);

  const doRefresh = useCallback(async () => {
    try {
      const r = await api.auth.refresh();
      setAccessToken(r.accessToken);
      const me = await api.auth.me();
      setUser(me.user);
      setStatus("authenticated");
      scheduleRefresh(r.accessExpiresIn);
    } catch {
      setAccessToken(null);
      setUser(null);
      setStatus("anonymous");
    }
  }, [scheduleRefresh]);

  // Boot : refresh silencieux + hook unauthorized
  useEffect(() => {
    void doRefresh();
    onUnauthorized(() => {
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
      setAccessToken(null);
      setUser(null);
      setStatus("anonymous");
    });
    return () => {
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
    };
  }, [doRefresh]);

  const login = useCallback(
    async (input: LoginInput): Promise<LoginResponse> => {
      const res = await api.auth.login(input);
      if (isLoginMfaChallenge(res)) {
        return res;
      }
      // Succès direct (LoginSuccess)
      setAccessToken(res.accessToken);
      setUser(res.user);
      setStatus("authenticated");
      scheduleRefresh(res.accessExpiresIn);
      return res;
    },
    [scheduleRefresh],
  );

  const completeMfa = useCallback(
    async (mfaToken: string, code: string): Promise<void> => {
      const res = await api.auth.completeMfa({ mfaToken, code });
      setAccessToken(res.accessToken);
      setUser(res.user);
      setStatus("authenticated");
      scheduleRefresh(res.accessExpiresIn);
    },
    [scheduleRefresh],
  );

  const logout = useCallback(async () => {
    try {
      await api.auth.logout();
    } catch {
      /* on continue même si l'appel échoue */
    }
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    setAccessToken(null);
    setUser(null);
    setStatus("anonymous");
    // C.4 · Vider la DB locale pour ne pas leaker l'outbox au prochain user.
    // L'agent voit son outbox via le badge réseau ; un logout volontaire
    // sans sync préalable est un choix éclairé.
    await clearOfflineDb();
  }, []);

  const refreshMe = useCallback(async () => {
    if (!getAccessToken()) return;
    const me = await api.auth.me();
    setUser(me.user);
  }, []);

  const value = useMemo<AuthCtx>(
    () => ({ status, user, login, completeMfa, logout, refreshMe }),
    [status, user, login, completeMfa, logout, refreshMe],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
